import {useFrame, useThree} from "@react-three/fiber";
import React, {useCallback, useMemo, useRef} from "react";
import * as THREE from "three";
import {GetCameraWorldPos} from "../../../../../Utils/ThreeJS/3JSHooks.js";
import {LHQInstance} from "../../LHQ/Scene.js";
import {AlarmClock, AlarmClock_Simple} from "./AlarmClockCatcher/AlarmClock.js";
import {Bed} from "./AlarmClockCatcher/Bed.js";
import {makeObservable} from "mobx";
import {O, RunInAction} from "web-vcore";
import {store} from "../../../../../Store/index.js";
import {RCEffectPlayRequest} from "../../../../../Store/main/tools/vr.js";
import {GetRandomNumber} from "js-vextensions";

export const AlarmClockCatcherSet = ((props: {lhq: LHQInstance})=>{
	return <AlarmClockCatcherSystem maxActivePairs={30}/>;
});

interface ClockCatcherProps {
	maxActivePairs: number; // Total pairs available (more than active to allow storage)
}
export function AlarmClockCatcherSystem({maxActivePairs}: ClockCatcherProps) {
	const camera = useThree(a=>a.camera);
	const groupRef = useRef<THREE.Group>(null);

	const uiState = store.main.tools.vr;

	// Play clock clink sound
	const playClink = ()=>{
		if (!audioContext.current) {
			audioContext.current = new (window.AudioContext || (window as any).webkitAudioContext)();
		}

		const oscillator = audioContext.current.createOscillator();
		const gainNode = audioContext.current.createGain();

		oscillator.connect(gainNode);
		gainNode.connect(audioContext.current.destination);

		oscillator.frequency.setValueAtTime(800, audioContext.current.currentTime);
		oscillator.frequency.exponentialRampToValueAtTime(400, audioContext.current.currentTime + 0.1);

		gainNode.gain.setValueAtTime(0.3, audioContext.current.currentTime);
		gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.current.currentTime + 0.1);

		oscillator.start();
		oscillator.stop(audioContext.current.currentTime + 0.1);
	};

	// Update the data structures
	const pairs = useRef<Array<{
		active: boolean;
		elapsedTime: number;
		preCatchTime: number;
		postCatchTime: number;
		caught: boolean;

		// Clock trajectory data
		clockInitialPos: THREE.Vector3;
		clockHorizontalVelocity: THREE.Vector3;
		clockInitialVerticalVelocity: number;
		clockInitialRotation: number;
		clockRotationSpeed: number;

		// Bed trajectory data
		bedInitialPos: THREE.Vector3;
		bedVelocity: THREE.Vector3;
		bedInitialRotation: number;
		bedRotationSpeed: number;

		// Catch data
		catchLocation: THREE.Vector3;
		catchRotation: number;

		clockMesh: THREE.Mesh | null;
		bedMesh: THREE.Group | null;
	}>>([]);

	const audioContext = useRef<AudioContext | null>(null);
	const MAX_DURATION = 4; // seconds - longest possible duration

	// Initialize arrays
	useMemo(()=>{
		pairs.current = Array.from({length: maxActivePairs}, ()=>({
			active: false,
			elapsedTime: 0,
			preCatchTime: 0,
			postCatchTime: 0,
			caught: false,

			clockInitialPos: new THREE.Vector3(),
			clockHorizontalVelocity: new THREE.Vector3(),
			clockInitialVerticalVelocity: 0,
			clockInitialRotation: 0,
			clockRotationSpeed: 0,

			bedInitialPos: new THREE.Vector3(),
			bedVelocity: new THREE.Vector3(),
			bedInitialRotation: 0,
			bedRotationSpeed: 0,

			catchLocation: new THREE.Vector3(),
			catchRotation: 0,

			clockMesh: null,
			bedMesh: null
		}));
	}, [maxActivePairs]);

	const CATCH_HEIGHT = 0.5; // target height to catch the clock (bed height)
	const BED_FEET_TO_SURFACE_DIST = 0.3; // distance from bed feet to surface (so we can lower the bed object's position, and get its surface to be at y=0.5)

	// Modified spawnPair function
	const spawnPair = (request: RCEffectPlayRequest)=>{
		const pairIndex = pairs.current.findIndex(p=>!p.active);
		if (pairIndex === -1) return;
		const pair = pairs.current[pairIndex];
		console.log(`=== Spawning pair ${pairIndex} ===`);

		// === STEP 1: Find initial clock position and trajectory ===
		const clockInitialPos = request.rightControllerPosition.clone();
		const clockDirection = request.rightControllerDirection.clone().normalize();

		console.log(`Clock initial position: (${clockInitialPos.x.toFixed(2)}, ${clockInitialPos.y.toFixed(2)}, ${clockInitialPos.z.toFixed(2)})`);
		console.log(`Clock direction: (${clockDirection.x.toFixed(3)}, ${clockDirection.y.toFixed(3)}, ${clockDirection.z.toFixed(3)})`);

		// === STEP 2: Select random clock move speed (horizontal only) ===
		const clockMoveSpeed = GetRandomNumber({min: 7, max: 10}); // clock move-speed (including vertical component), in meters/second
		// z-up migration: vertical is now z, horizontal plane is x/y. (mapping for y-up->z-up scene rotation: (x,y,z)_old -> (x,-z,y)_new)
		// note: controller position/direction currently come from XR space (still y-up internally); revisit once XR origin is converted.
		const clockVelocity = clockDirection.clone().multiplyScalar(clockMoveSpeed);
		const clockHorizontalVelocity = new THREE.Vector3(clockVelocity.x, -clockVelocity.z, 0);
		const clockInitialVerticalVelocity = clockVelocity.z;

		console.log(`Clock move speed: ${clockMoveSpeed.toFixed(2)} m/s`);
		console.log(`Clock horizontal velocity: (${clockHorizontalVelocity.x.toFixed(2)}, 0, ${clockHorizontalVelocity.z.toFixed(2)})`);
		console.log(`Clock initial vertical velocity: ${clockInitialVerticalVelocity.toFixed(2)} m/s`);

		// === STEP 3: Calculate catch point using physics ===

		// === STEP 3.1: Set up quadratic equation to find catch time ===
		// physics: z = z0 + v0*t - 0.5*g*t^2; standard form: 0.5*g*t^2 - v0*t + (z0 - z_target) = 0
		const g = 9.8; // gravity acceleration (m/s²)
		const a = 0.5 * g; // coefficient for t² term (4.9)
		const b = -clockInitialVerticalVelocity; // coefficient for t term (negative of initial vertical velocity)
		// z-up: solve for when the clock reaches z = CATCH_HEIGHT
		const c = CATCH_HEIGHT - clockInitialPos.z; // constant term (target height - initial height)

		// Calculate discriminant to check if solution exists
		const quadraticSolutionDiscriminant = b*b - 4*a*c;
		if (quadraticSolutionDiscriminant < 0) {
			console.log("No solution for catch time - clock will never reach y=0.2");
			return;
		}

		const t1 = (-b + Math.sqrt(quadraticSolutionDiscriminant)) / (2*a);
		const t2 = (-b - Math.sqrt(quadraticSolutionDiscriminant)) / (2*a);
		const preCatchTime = t1 > 0 && t2 > 0 ? Math.min(t1, t2) : (t1 > 0 ? t1 : t2); // Take the first positive time
		if (preCatchTime <= 0) {
			console.log("No valid positive catch time found");
			return;
		}

		console.log(`Catch time solutions: t1=${t1.toFixed(3)}, t2=${t2.toFixed(3)}, selected: ${preCatchTime.toFixed(3)}`);

		// Calculate catch location
		const catchLocation = clockInitialPos.clone()
			.add(clockHorizontalVelocity.clone().multiplyScalar(preCatchTime));
		catchLocation.z = CATCH_HEIGHT; // z-up: ensure exact catch height (vertical = z)

		console.log(`Catch location: (${catchLocation.x.toFixed(2)}, ${catchLocation.y.toFixed(2)}, ${catchLocation.z.toFixed(2)})`);

		// === STEP 4: Calculate clock rotation to face camera at catch time ===
		const camPos = GetCameraWorldPos(camera);
		const directionToCamera = camPos.clone().sub(catchLocation);
		const catchRotation = Math.atan2(directionToCamera.y, directionToCamera.x); // z-up: yaw is rotation about z

		// Select random initial rotation, then calculate required rotation speed
		const initialClockRotation = GetRandomNumber({min: 0, max: Math.PI * 2});
		const rotationDifference = catchRotation - initialClockRotation;
		// Normalize rotation difference to [-π, π]
		const normalizedRotDiff = ((rotationDifference + Math.PI) % (2 * Math.PI)) - Math.PI;
		const clockRotationSpeed = normalizedRotDiff / preCatchTime;

		console.log(`Clock rotation: initial=${initialClockRotation.toFixed(3)}, catch=${catchRotation.toFixed(3)}, speed=${clockRotationSpeed.toFixed(3)} rad/s`);

		// === STEP 5: Calculate bed movement ===
		const postCatchTime = GetRandomNumber({min: 2, max: 3});
		const bedTravelAngle = GetRandomNumber({min: 0, max: Math.PI * 2});
		const bedSpeed = GetRandomNumber({min: 1, max: 10});
		const bedRotationSpeed = GetRandomNumber({min: -5, max: 5}); // -2 to +2 radians/second

		const bedDirection = new THREE.Vector3(Math.cos(bedTravelAngle), Math.sin(bedTravelAngle), 0); // z-up: horizontal plane is x/y
		const bedVelocity = bedDirection.clone().multiplyScalar(bedSpeed);

		// Calculate bed initial position and rotation to be at catch location at catch time
		const bedInitialPos = catchLocation.clone().sub(bedVelocity.clone().multiplyScalar(preCatchTime));
		bedInitialPos.z = CATCH_HEIGHT - BED_FEET_TO_SURFACE_DIST; // z-up: vertical is z
		const initialBedRotation = catchRotation - (bedRotationSpeed * preCatchTime);

		// Store all calculated data in the pair
		pair.clockInitialPos.copy(clockInitialPos);
		pair.clockHorizontalVelocity.copy(clockHorizontalVelocity);
		pair.clockInitialVerticalVelocity = clockInitialVerticalVelocity;
		pair.clockInitialRotation = initialClockRotation;
		pair.clockRotationSpeed = clockRotationSpeed;

		pair.bedInitialPos.copy(bedInitialPos);
		pair.bedVelocity.copy(bedVelocity);
		pair.bedInitialRotation = initialBedRotation;
		pair.bedRotationSpeed = bedRotationSpeed;

		pair.catchLocation.copy(catchLocation);
		pair.catchRotation = catchRotation;

		pair.preCatchTime = preCatchTime;
		pair.postCatchTime = postCatchTime;
		pair.elapsedTime = 0;
		pair.caught = false;
		pair.active = true;

		// Make meshes visible
		if (pair.clockMesh) pair.clockMesh.visible = true;
		if (pair.bedMesh) pair.bedMesh.visible = true;

		console.log(`Spawned pair with catch time: ${preCatchTime.toFixed(3)}s, post-catch time: ${postCatchTime.toFixed(3)}s`);
	};

	// Update the useFrame logic
	useFrame((state, delta)=>{
		if (g.pauseRCE) return;

		// Check for new requests in pairRequestStore
		const newRequests = uiState.rcEffects_playRequestsToProcess;
		if (newRequests.length) {
			console.log(`Processing ${newRequests.length} new pair request(s)`);

			// Spawn pairs for each new request (up to available slots and active limit)
			for (const request of newRequests) {
				const currentlyActive = pairs.current.filter(p=>p.active).length;
				if (currentlyActive < maxActivePairs) {
					spawnPair(request);
				} else {
					console.log(`Cannot spawn more pairs - already at active limit (${maxActivePairs})`);
					break;
				}
			}

			//RunInAction("AlarmClockCatcherSystem.clearPlayRequests", ()=>uiState.rcEffects_playRequestsToProcess.clear());
			uiState.rcEffects_playRequestsToProcess.Clear();
		}

		// Update all active pairs
		pairs.current.forEach((pair, i)=>{
			if (!pair.active) return;

			// Advance elapsed time
			pair.elapsedTime += delta;

			// Check if pair is complete
			const totalTime = pair.preCatchTime + pair.postCatchTime;
			if (pair.elapsedTime >= totalTime) {
				pair.active = false;
				if (pair.clockMesh) pair.clockMesh.visible = false;
				if (pair.bedMesh) pair.bedMesh.visible = false;
				return;
			}

			// Check if we just caught the clock
			if (!pair.caught && pair.elapsedTime >= pair.preCatchTime) {
				pair.caught = true;
				playClink();
				console.log(`Pair ${i} - clock caught!`);
			}

			// Calculate current positions and rotations
			const t = pair.elapsedTime;

			// Update bed position and rotation
// z-up: spin about the vertical axis is rotation.z
				if (pair.bedMesh) {
					pair.bedMesh.position.copy(pair.bedInitialPos);
					pair.bedMesh.position.add(pair.bedVelocity.clone().multiplyScalar(t));
					pair.bedMesh.rotation.z = pair.bedInitialRotation + (pair.bedRotationSpeed * t);
			}

			// Update clock position and rotation
			if (pair.clockMesh) {
				if (pair.caught) {
					// Clock is caught - move with bed
					pair.clockMesh.position.copy(pair.bedMesh!.position);
					// z-up: keep at catch height (vertical = z)
					pair.clockMesh.position.z = CATCH_HEIGHT;
					pair.clockMesh.rotation.z = pair.bedMesh!.rotation.z;
				} else {
					// Clock is still flying - use physics trajectory
					// z-up: vertical = z (gravity along -z)
					pair.clockMesh.position.copy(pair.clockInitialPos);
					pair.clockMesh.position.add(pair.clockHorizontalVelocity.clone().multiplyScalar(t));
					pair.clockMesh.position.z = pair.clockInitialPos.z + (pair.clockInitialVerticalVelocity * t) - (0.5 * 9.8 * t * t);
					pair.clockMesh.rotation.z = pair.clockInitialRotation + (pair.clockRotationSpeed * t);
				}
			}
		});

	});

	return (
		<group ref={groupRef}>
			{pairs.current.map((_, i)=>{
				return <AlarmClock_Simple key={`clock-${i}`} extraProcessing_late={useCallback(mesh=>{
					if (mesh) pairs.current[i].clockMesh = mesh;
				}, [i])}/>;
			})}
			{pairs.current.map((_, i)=>{
				return <Bed key={`bed-${i}`} extraProcessing_late={useCallback(mesh=>{
					if (mesh) pairs.current[i].bedMesh = mesh;
				}, [i])}/>;
			})}
		</group>
	);
}